/**
 * 中国省市区数据
 * 使用 province-city-china 库提供完整的省市区数据
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const pccData = require('province-city-china/data');

interface RegionOption {
  value: string;
  label: string;
  children?: RegionOption[];
}

interface RegionData {
  code: string;
  name: string;
  province: string;
  city: number | string;
  area: number | string;
  town: number | string;
}

/**
 * 将省市区数据转换为 Cascader 组件所需的格式
 */
export const getRegionData = (): RegionOption[] => {
  const allData = pccData.data as RegionData[];
  
  if (!allData || !Array.isArray(allData)) {
    console.error('Failed to load region data');
    return [];
  }
  
  // 获取所有省份（city和area都为0的数据）
  const provinces = allData.filter((item: RegionData) => 
    item.city === 0 && item.area === 0
  );
  
  const provinceOptions = provinces.map((province: RegionData) => ({
    value: province.name,
    label: province.name,
    children: getCitiesByProvince(allData, province.code),
  }));
  
  // 将浙江、江苏、上海放在最前面
  const priorityProvinces = ['浙江省', '江苏省', '上海市', '浙江', '江苏', '上海'];
  const priorityOptions: RegionOption[] = [];
  const otherOptions: RegionOption[] = [];
  
  provinceOptions.forEach(option => {
    // 检查是否匹配优先省份（支持带省/市后缀和不带后缀）
    const isPriority = priorityProvinces.some(name => 
      option.value === name || 
      option.value.startsWith(name) ||
      name.startsWith(option.value)
    );
    
    if (isPriority) {
      priorityOptions.push(option);
    } else {
      otherOptions.push(option);
    }
  });
  
  // 按照指定顺序排列优先省份（浙江、江苏、上海）
  const orderedPriorityOptions: RegionOption[] = [];
  ['浙江', '江苏', '上海'].forEach(name => {
    const found = priorityOptions.find(opt => 
      opt.value.includes(name) || name.includes(opt.value)
    );
    if (found && !orderedPriorityOptions.includes(found)) {
      orderedPriorityOptions.push(found);
    }
  });
  
  return [...orderedPriorityOptions, ...otherOptions];
};

/**
 * 根据省份代码获取城市列表
 */
const getCitiesByProvince = (allData: RegionData[], provinceCode: string): RegionOption[] => {
  const provincePrefix = provinceCode.substring(0, 2);
  
  // 获取该省份下的所有城市（前两位匹配，city不为0，area为0）
  const cities = allData.filter((item: RegionData) => 
    item.code.startsWith(provincePrefix) && 
    item.city !== 0 && 
    item.area === 0
  );
  
  return cities.map((city: RegionData) => ({
    value: city.name,
    label: city.name,
    children: getDistrictsByCity(allData, city.code),
  }));
};

/**
 * 根据城市代码获取区县列表
 */
const getDistrictsByCity = (allData: RegionData[], cityCode: string): RegionOption[] => {
  const cityPrefix = cityCode.substring(0, 4);
  
  // 获取该城市下的所有区县（前四位匹配，area不为0，town为0，排除街道）
  const districts = allData.filter((item: RegionData) => 
    item.code.startsWith(cityPrefix) && 
    item.area !== 0 &&
    item.town === 0  // 排除街道级别
  );
  
  return districts.map((district: RegionData) => ({
    value: district.name,
    label: district.name,
  }));
};

// 导出完整的地区数据（每次都重新生成，确保排序生效）
export const REGION_DATA = (() => {
  try {
    const data = getRegionData();
    console.log('Region data loaded, first 3 provinces:', data.slice(0, 3).map(p => p.value));
    return data;
  } catch (error) {
    console.error('Failed to initialize region data:', error);
    return [];
  }
})();
